import { useMemo } from 'react'

import { InviteMemberForm, TeamMemberDto } from '@api'
import { useMutation, useQueryClient } from 'react-query'

import { teamService } from '@/modules/team/service/team-service.ts'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import type { TeamInviteMemberRequest } from '../types'

export const useUpdateTeamTable = () => {
  const queryClient = useQueryClient()

  const updateMutation = useMutation(
    (data: Partial<TeamMemberDto>) => teamService.changeUserById(data),
    {
      onMutate: async (updatedData: Partial<TeamMemberDto>) => {
        const previousTeamData =
          queryClient.getQueryData<TeamMemberDto[]>('team')

        // Optimistically updates the team data in the query cache
        if (previousTeamData) {
          const filteredTeamData = previousTeamData.filter(isTruthyAndNotEmpty)

          const updatedTeamData = filteredTeamData.map((prevMember) => {
            if (prevMember.id === updatedData.id) {
              return updatedData
            }

            return prevMember
          })

          queryClient.setQueryData('team', updatedTeamData)
        }

        return previousTeamData
      },
      onError: () => {
        queryClient.invalidateQueries('team')
      },
    },
  )

  const deleteMutation = useMutation(
    (id: number) => teamService.removeUserById(id),
    {
      onMutate: (userId) => {
        queryClient.setQueryData<TeamMemberDto[] | undefined>(
          'team',
          (prevMembers) => {
            return prevMembers?.filter((member) => member.id !== userId)
          },
        )
      },
      onError: () => {
        queryClient.invalidateQueries('team')
      },
    },
  )

  const inviteMutation = useMutation(
    (inviteUserData: InviteMemberForm[]) =>
      teamService.inviteUser(inviteUserData),
    {
      onSettled: () => {
        queryClient.invalidateQueries('team')
      },
    },
  )

  return useMemo(
    () => ({
      updateMember: updateMutation,
      deleteMember: deleteMutation,
      inviteMember: inviteMutation,
    }),
    [updateMutation, deleteMutation, inviteMutation],
  )
}
